import { useState } from "react";
import type { SecurityFinding } from "../api";

// ─── Security Findings Tab ─────────────────────────────────────
// Shows findings grouped by severity with fix + test snippets.
// Includes first-time banner explaining pre-existing findings.

interface Props {
  findings: SecurityFinding[];
  baselineAt: string | null;
  score: number | null;
}

const SEVERITY_ORDER = ["Critical", "High", "Medium", "Low"] as const;

const SEVERITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Critical: { bg: "rgba(239, 68, 68, 0.08)", text: "#ef4444", border: "#ef4444" },
  High: { bg: "rgba(0, 229, 255, 0.08)", text: "#00E5FF", border: "#00E5FF" },
  Medium: { bg: "rgba(234, 179, 8, 0.08)", text: "#eab308", border: "#eab308" },
  Low: { bg: "rgba(59, 130, 246, 0.08)", text: "#3b82f6", border: "#3b82f6" },
};

function scoreColor(score: number): string {
  if (score >= 80) return "#22c55e";
  if (score >= 50) return "#eab308";
  return "#ef4444";
}

export default function SecurityFindings({ findings, baselineAt, score }: Props) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [dismissedBanner, setDismissedBanner] = useState(false);

  const grouped = SEVERITY_ORDER.map(sev => ({
    severity: sev,
    items: findings.filter(f => f.severity === sev),
  })).filter(g => g.items.length > 0);

  const toggle = (idx: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>🔒</span> Security Findings
        </h2>
        {score != null && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "var(--bg-card)", borderRadius: 8, padding: "0.5rem 1rem",
            border: "1px solid var(--border)",
          }}>
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>Security Score</span>
            <span style={{ fontSize: "var(--text-xl, 1.25rem)", fontWeight: 700, color: scoreColor(score) }}>
              {score}/100
            </span>
          </div>
        )}
      </div>

      {/* First-time banner */}
      {baselineAt && !dismissedBanner && (
        <div style={{
          background: "rgba(59, 130, 246, 0.08)",
          border: "1px solid rgba(59, 130, 246, 0.3)",
          borderRadius: 8,
          padding: "1rem 1.25rem",
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Pre-existing findings</div>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
              This scan was completed on{" "}
              <strong>{new Date(baselineAt).toLocaleDateString()}</strong> before any OCNexus
              changes were applied. All findings listed below pre-existed in the repository.
            </div>
          </div>
          <button
            onClick={() => setDismissedBanner(true)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-muted)", fontSize: 16, padding: 4,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Summary badges */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        {SEVERITY_ORDER.map(sev => {
          const count = findings.filter(f => f.severity === sev).length;
          if (count === 0) return null;
          const c = SEVERITY_COLORS[sev];
          return (
            <div key={sev} style={{
              background: c.bg, border: `1px solid ${c.border}`, borderRadius: 6,
              padding: "0.35rem 0.75rem", display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ fontWeight: 600, color: c.text, fontSize: "var(--text-sm)" }}>{count}</span>
              <span style={{ fontSize: "var(--text-xs)", color: c.text }}>{sev}</span>
            </div>
          );
        })}
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 6,
          padding: "0.35rem 0.75rem", display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>{findings.length}</span>
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Total</span>
        </div>
      </div>

      {/* No findings state */}
      {findings.length === 0 && (
        <div style={{
          padding: "3rem", textAlign: "center", color: "var(--text-muted)",
          background: "var(--bg-card)", borderRadius: 8,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div style={{ fontWeight: 600 }}>No security findings</div>
          <div style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
            The repository passed the security scan with no issues detected.
          </div>
        </div>
      )}

      {/* Grouped findings */}
      {grouped.map(group => (
        <div key={group.severity} style={{ marginBottom: "1.5rem" }}>
          <h3 style={{
            fontSize: "var(--text-base)", fontWeight: 600, marginBottom: "0.75rem",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{
              display: "inline-block", width: 10, height: 10, borderRadius: "50%",
              background: SEVERITY_COLORS[group.severity]?.border ?? "var(--text-muted)",
            }} />
            {group.severity} ({group.items.length})
          </h3>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {group.items.map((finding, i) => {
              const globalIdx = findings.indexOf(finding);
              const isOpen = expanded.has(globalIdx);
              const colors = SEVERITY_COLORS[finding.severity] ?? SEVERITY_COLORS.Low;

              return (
                <div
                  key={globalIdx}
                  style={{
                    background: "var(--bg-card)",
                    border: `1px solid ${isOpen ? colors.border : "var(--border)"}`,
                    borderRadius: 8,
                    overflow: "hidden",
                    transition: "border-color 0.15s",
                  }}
                >
                  {/* Header row */}
                  <button
                    onClick={() => toggle(globalIdx)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 10,
                      padding: "0.75rem 1rem", background: "none", border: "none",
                      cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: colors.text,
                      background: colors.bg, border: `1px solid ${colors.border}`,
                      borderRadius: 4, padding: "2px 6px", flexShrink: 0,
                    }}>
                      {finding.severity}
                    </span>
                    <span style={{ fontWeight: 500, flex: 1 }}>{finding.title}</span>
                    {finding.file && (
                      <span style={{
                        fontSize: 11, color: "var(--text-muted)",
                        fontFamily: "var(--font-mono, monospace)", flexShrink: 0,
                      }}>
                        {finding.file}
                      </span>
                    )}
                    <span style={{ color: "var(--text-muted)", fontSize: 14, flexShrink: 0 }}>
                      {isOpen ? "▾" : "▸"}
                    </span>
                  </button>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div style={{
                      padding: "0 1rem 1rem",
                      borderTop: "1px solid var(--border)",
                    }}>
                      {finding.evidence && (
                        <DetailSection label="Evidence" content={finding.evidence} />
                      )}
                      {finding.impact && (
                        <DetailSection label="Impact" content={finding.impact} />
                      )}
                      {finding.fix && (
                        <DetailSection label="Fix" content={finding.fix} isCode />
                      )}
                      {finding.test && (
                        <DetailSection label="Unit Test" content={finding.test} isCode />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailSection({ label, content, isCode }: { label: string; content: string; isCode?: boolean }) {
  return (
    <div style={{ marginTop: "0.75rem" }}>
      <div style={{
        fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--text-muted)",
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
      }}>
        {label}
      </div>
      {isCode ? (
        <pre style={{
          background: "rgba(0,0,0,0.15)", borderRadius: 6, padding: "0.75rem",
          fontSize: 12, fontFamily: "var(--font-mono, monospace)",
          overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
          lineHeight: 1.5, margin: 0,
        }}>
          {content}
        </pre>
      ) : (
        <div style={{ fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--text-primary, inherit)" }}>
          {content}
        </div>
      )}
    </div>
  );
}
